import React, { useState, useEffect } from "react";
import { useLocation, useParams } from "react-router-dom";
import Chart from "react-apexcharts";
import {
  GetBatchPredictionResult,
  handleFetchPredictorHeader,
  handleSubmitPrediction,
} from "../../modules/predictorModule.ts";
import {toast, ToastContainer} from 'react-toastify';import PredictionModal from "../../components/common/PredictionModal.tsx";
import { Autocomplete, Box, TextField } from "@mui/material";
import { useNavigate } from "react-router-dom";
import Button from "../../components/common/Button.tsx";
import BreadCrumb from "../../components/common/BreadCrumb.tsx";
import { CardBody, Col, Input, Row, Table } from "reactstrap";
import { LabelText } from "../../components/common/Labeltext.tsx";
import SelectField from "../../components/common/SelectField.tsx";

interface ColumnData {
  column_name: string;
  data_type: string;
  default_value: string;
}

interface PredictorResponse {
  id: number;
  headers: ColumnData[];
  target: string;
  preprocess_id: number;
}

interface FormField {
  name: string;
  dataType: string;
  inputValue: string;
  error: string;
}

const FormPrediction = () => {
  //const toast = useToast();
  const navigate = useNavigate();

  const { id } = useParams<{ id: string }>();
  const [fields, setFields] = useState<FormField[]>([]);
  const [loading, setLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [preprocessId, setPreprocessId] = useState<number | null>(null);
  const [target, setTarget] = useState("");
  const [isModalOpen, setIsModalOpen] = useState(false);
  const location = useLocation();
  const [predictionResult, setPredictionResult] = useState<{
    prediction: number | boolean;
    accuracy: number;
  } | null>(null);

  const booleanOptions = [
    { value: "true", label: "True" },
    { value: "false", label: "False" },
  ];
  const problem_type = location.state.problem_type
  // const problem_type = "Regression"

  useEffect(() => {
    if (!id) return;

    const fetchData = async () => {
      try {
        const response: PredictorResponse = await handleFetchPredictorHeader(id);
        setPreprocessId(response.preprocess_id);
        setTarget(response.target);

        let formattedFields = response.headers.map((col: ColumnData) => ({
          name: col.column_name,
          dataType: col.data_type,
          inputValue:
            col.data_type === "Boolean"
              ? col.default_value.toLowerCase()
              : col.default_value || "",
          error: "",
        }));

        if (location?.state?.prediction_id) {
          try {
            const predictionResp = await GetBatchPredictionResult(
              location.state.prediction_id
            );

            formattedFields = formattedFields.map((field) => ({
              ...field,
              inputValue:
                predictionResp.input_features[field.name] ?? field.inputValue,
            }));
            setPredictionResult(predictionResp.prediction_output)
            if (problem_type !== "Regression" && problem_type !== "UnsupervisedLearning") {
              setIsModalOpen(true)
            }
          } catch (error) {
            const errorMessage =
              error?.response?.data?.detail ||
              "Failed to view batch prediction result";
            toast.error(errorMessage);
          }
        }

        setFields(formattedFields);
      } catch (error) {
        console.error("Error fetching columns:", error);
        toast.error("Failed to load form data");
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [id, toast, location?.state?.prediction_id]);

  const validateInput = (value: string, type: string): string => {
    if (!value) return "This field is required";

    switch (type) {
      case "Integer":
        return isNaN(Number(value)) || !Number.isInteger(Number(value))
          ? "Please enter a valid integer"
          : "";
      case "Decimal":
      case "Number":
      case "Float":
        return isNaN(Number(value)) ? "Please enter a valid number" : "";
      case "Datetime":
        return !Date.parse(value) ? "Please enter a valid date and time" : "";
      case "Boolean":
        return value !== "true" && value !== "false"
          ? "Please select true or false"
          : "";
      default:
        return "";
    }
  };

  const formatTimestamp = (dateString: string): string => {
    const date = new Date(dateString);
    const day = String(date.getDate()).padStart(2, "0");
    const month = date.toLocaleString("en-US", { month: "short" });
    const year = String(date.getFullYear()).slice(-2);
    return `${day}-${month}-${year}`;
  };

  const formatValueForPayload = (value: string, type: string) => {
    switch (type) {
      case "Integer":
        return parseInt(value, 10);
      case "Decimal":
      case "Number":
      case "Float":
        return Number(value);
      case "Boolean":
        return value === "true";
      case "Datetime":
        return formatTimestamp(value);
      default:
        return value;
    }
  };

  const handleInputChange = (
    fieldName: string,
    value: string,
    type: string
  ) => {
    const error = validateInput(value, type);

    setFields((prev) =>
      prev.map((field) =>
        field.name === fieldName
          ? { ...field, inputValue: value, error }
          : field
      )
    );
  };

  const getInputType = (dataType: string): string => {
    switch (dataType) {
      case "Integer":
      case "Decimal":
      case "Number":
      case "Float":
        return "number";
      case "Datetime":
        return "datetime-local";
      default:
        return "text";
    }
  };

  const renderInput = (field: FormField) => {
    if (field.dataType === "Boolean") {
      const selectedOption = booleanOptions.find(
        (option) => option.value === field.inputValue
      );

      return (
        <SelectField
          value={selectedOption || null}   // ✅ correct
          options={booleanOptions}
          isClearable={false}
          handleChange={(selectedOption) => {
            handleInputChange(field.name, selectedOption?.value || "", field.dataType);
          }}
          isRVStyle={true}
        />
      );
    }

    const inputType = getInputType(field.dataType);

    return (
      <Input
        type={inputType}
        step={field.dataType === "Datetime" ? "1" : undefined}
        value={field.inputValue}
        onChange={(e) =>
          handleInputChange(field.name, e.target.value, field.dataType)
        }
        placeholder={`Enter ${field.dataType.toLowerCase()}`}
      />
    );
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const validatedFields = fields.map((field) => ({
      ...field,
      error: validateInput(field.inputValue, field.dataType),
    }));

    setFields(validatedFields);

    const hasErrors = validatedFields.some((field) => field.error);
    if (hasErrors) {
      toast.error("Please correct all errors before submitting");
      return;
    }

    if (!preprocessId) {
      toast.error("Invalid Preprocess ID");
      return;
    }

    try {
      setIsSubmitting(true);
      const features = fields.reduce((acc, field) => {
        acc[field.name] = formatValueForPayload(
          field.inputValue,
          field.dataType
        );
        return acc;
      }, {} as Record<string, any>);

      const response = await handleSubmitPrediction(
        preprocessId,
        features,
        target,
        parseInt(id || ""),
      );
      const result = {
        // prediction: response.prediction,
        prediction: "Anomaly",
        accuracy: response.accuracy || response.confidence || 0,
      };
      setPredictionResult(result);
      if (problem_type !== "Regression" && problem_type !== "UnsupervisedLearning") {
        setIsModalOpen(true)
      }
    } catch (error) {
      toast.error("Prediction Failed!");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancel = () => {
    navigate('/TrainingModal');
  };

  return (
    <>
      <BreadCrumb moduleName={"Training"} pageName={"Form Prediction"} viewName={location?.state?.name} />
      <ToastContainer position="top-center" />
      <div className="listingjs-table " id="customerList">
        <div className="live-preview">
          <div className="align-items-center d-flex mb-3 ">
            <h4 className="card-title mb-0 flex-grow-1">Form Based Prediction</h4>
          </div>
          {loading ? (
            <div className="flex justify-center items-center h-40">
              <div className="w-8 h-8 border-4 border-[#2F99A8] border-t-transparent rounded-full animate-spin"></div>
            </div>
          ) : (
            <>
              {(problem_type === "Regression" || problem_type === "UnsupervisedLearning") && predictionResult && <div className="relative border-2 rounded-lg p-3 md:p-5 mb-5">
                <h5 className="text-xl font-bold mb-4 md:mb-6">
                  Prediction Completed
                </h5>

                {problem_type === "Regression" ? (
                  <div className="d-flex align-items-center justify-content-center gap-5">

                    {/* LEFT — Donut with center score */}
                    {predictionResult?.accuracy != null && (
                      <div style={{ width: 160, position: "relative" }}>

                        <Chart
                          options={{
                            labels: ["Score", "Remaining"],
                            colors: ["#5A7FA5", "#E9ECEF"],
                            dataLabels: { enabled: false },
                            legend: { show: false },
                            plotOptions: {
                              pie: {
                                donut: { size: "78%" },
                              },
                            },
                          }}
                          series={[
                            predictionResult.accuracy,
                            100 - predictionResult.accuracy
                          ]}
                          type="donut"
                          height={160}
                        />

                        {/* CENTER VALUE OVERLAY */}
                        <div
                          style={{
                            position: "absolute",
                            top: "50%",
                            left: "50%",
                            transform: "translate(-50%, -50%)",
                            textAlign: "center",
                            pointerEvents: "none",
                          }}
                        >
                          <div style={{ fontSize: 28, fontWeight: 600 }}>
                            {Math.round(predictionResult.accuracy)}
                          </div>
                          <div style={{ fontSize: 12, color: "#777" }}>
                            /100
                          </div>
                        </div>

                      </div>
                    )}

                    {/* RIGHT — Text + Progress */}
                    <div style={{ maxWidth: 320 }}>
                      <h5 className="fw-medium mb-1">
                        Network QoE Score
                      </h5>

                      <p className="text-muted mb-3" style={{ fontSize: 14 }}>
                        Quality of Experience rating based on
                        customer data analysis
                      </p>

                      {/* Progress bar */}
                      <div className="progress" style={{ height: 30, borderRadius: "5px" }}>
                        <div
                          className="progress-bar"
                          style={{
                            width: `${predictionResult?.accuracy || 0}%`,
                            backgroundColor: "#5A7FA5",
                          }}
                        />
                      </div>
                    </div>

                  </div>
                ) : (
                  <div className="d-flex align-items-center justify-content-center gap-4">

                    {/* ICON CIRCLE */}
                    {/* <div
                      style={{
                        width: 120,
                        height: 120,
                        borderRadius: "50%",
                        background: predictionResult?.prediction?.toLowerCase() === "anomaly"
                          ? "linear-gradient(135deg, #F28C38, #E6472E)"
                          : "linear-gradient(135deg, #38C976, #1FAF5A)",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        boxShadow: "0 8px 18px rgba(0,0,0,0.15)",
                        color: "#fff",
                        fontSize: 48,
                      }}
                    >
                      {predictionResult?.prediction?.toLowerCase() === "anomaly" ? "⚠️" : "✓"}
                    </div> */}
                    <div
                      style={{
                        width: 120,
                        height: 120,
                        borderRadius: "50%",
                        background: predictionResult?.prediction?.toLowerCase() === "anomaly"
                          ? "linear-gradient(135deg, #F28C38, #E6472E)"
                          : "linear-gradient(135deg, #38C976, #1FAF5A)",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        boxShadow: "0 8px 18px rgba(0,0,0,0.15)",
                      }}
                    >
                      {predictionResult?.prediction?.toLowerCase() === "anomaly" ? (
                        // WHITE WARNING ICON
                        <svg width="48" height="48" viewBox="0 0 24 24" fill="white">
                          <path d="M1 21h22L12 2 1 21z" />
                          <rect x="11" y="9" width="2" height="5" fill="#E6472E" />
                          <rect x="11" y="16" width="2" height="2" fill="#E6472E" />
                        </svg>
                      ) : (
                        // WHITE CHECK ICON
                        <svg width="48" height="48" viewBox="0 0 24 24" fill="white">
                          <path d="M9 16.2l-3.5-3.5L4 14.2l5 5 12-12-1.5-1.5z" />
                        </svg>
                      )}
                    </div>


                    {/* TEXT BLOCK */}
                    <div>
                      <div className="text-uppercase fw-medium mb-1">
                        Anomaly Prediction
                      </div>

                      <div
                        style={{
                          fontSize: 44,
                          fontWeight: 500,
                          color: predictionResult?.prediction?.toLowerCase() === "anomaly" ? "#D44723" : "#1FAF5A",
                        }}
                      >
                        {predictionResult?.prediction}
                      </div>
                    </div>

                  </div>

                )}

              </div>
              }

              <CardBody>
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div className="table-border border p-3">
                    <div className=''>
                      <Table className="table-borderless align-middle table-nowrap ms-0 mb-0 overflow-auto">
                        <thead>
                          <tr>
                            <th className="px-0 mx-0" scope="col">Data Column Name</th>
                            <th scope="px-0 mx-0">Data Type</th>
                            <th scope="px-0 mx-0">Input Value</th>
                          </tr>
                        </thead>
                        <tbody>
                          {fields.map((field, index) => (
                            <tr key={index}>
                              {/* Column Name */}
                              <td>
                                <div className="bg-light rounded-lg p-2 small text-break overflow-hidden">
                                  <div className="text-truncate" title={field.name}>
                                    {field.name}
                                  </div>
                                </div>
                              </td>

                              {/* Data Type */}
                              <td>
                                <div className="bg-light rounded-lg p-2 small text-break overflow-hidden">
                                  <div className="text-truncate" title={field.dataType}>
                                    {field.dataType}
                                  </div>
                                </div>
                              </td>

                              {/* Input */}
                              <td>
                                {renderInput(field)}
                                {field.error && field.dataType !== "Boolean" && (
                                  <span className="text-danger small d-block mt-1">
                                    {field.error}
                                  </span>
                                )}
                              </td>
                            </tr>
                          ))}
                        </tbody>

                      </Table>
                      <Row lg={6} className="justify-content-center mt-4">
                        <div className="live-preview">
                          <div className="d-flex justify-content-center gap-2">
                            <button
                              type="button"
                              className="btn-label rv-secondary-btn rounded-1 px-1 ps-4 py-1 w-md-auto text-nowrap btn btn-secondary"
                              onClick={handleCancel}
                            >
                              <i className="ri-close-line label-icon secondary-label-icon align-middle fs-16 me-2"></i>
                              <span className="px-2 ms-2 me-1 fw-bold">
                                Cancel
                              </span>
                            </button>
                            <button
                              onClick={handleSubmit}
                              type="submit"
                              className="btn-label rv-primary-btn rounded-1 btn btn-secondary d-flex align-items-center"
                              style={{ height: 'auto', minHeight: 'fit-content' }}
                              disabled={isSubmitting}
                            >
                              <i className="ri-play-fill label-icon align-middle fs-16 me-1"></i>
                              <span className="ms-2 me-1 fw-bold d-inline-flex align-items-center gap-2 text-nowrap">
                                {isSubmitting ? (
                                  <>
                                    <div className="spinner-border spinner-border-sm text-light" role="status">
                                      <span className="visually-hidden">Loading...</span>
                                    </div>
                                    <span>Please wait</span>
                                  </>
                                ) : (
                                  "Run Prediction"
                                )}
                              </span>
                            </button>
                          </div>
                        </div>
                      </Row>
                    </div>
                  </div>
                </form>
              </CardBody>
            </>
          )}
          {isModalOpen && predictionResult && (
            <PredictionModal
              isOpen={isModalOpen}
              onClose={() => setIsModalOpen(false)}
              predictionResult={predictionResult}
            />
          )}
        </div>
      </div >
    </>
    // <>
    // <BreadCrumb moduleName={"Training"} pageName={"Form Prediction"} viewName={location?.state?.name}/>
    // <div className="md:p-0 w-full max-w-[1200px] mx-auto">
    //   <h1 className="text-base font-bold mb-4 md:mb-6">
    //     FORM BASED PREDICTION
    //   </h1>
    //   {loading ? (
    //     <div className="flex justify-center items-center h-40">
    //       <div className="w-8 h-8 border-4 border-[#2F99A8] border-t-transparent rounded-full animate-spin"></div>
    //     </div>
    //   ) : (
    //     <>
    //       {(problem_type === "Regression"|| problem_type === "UnsupervisedLearning") && predictionResult && <div className="relative border-2 rounded-lg p-3 md:p-5 mb-5">
    //         <h5 className="text-xl font-bold mb-4 md:mb-6">
    //           Prediction Completed
    //         </h5>

    //         {problem_type === "Regression" ? <div className="flex">
    //           <div className="w-1/2">
    //             <h1 className="text-lg font-bold mb-2">Network QoE score</h1>
    //             <h6 className="font-bold" style={{fontSize: "40px"}}>{Math.round(predictionResult?.prediction * 100) / 100}</h6>
    //           </div>
    //           <div className="w-px bg-gray-500"></div>
    //           <div className="w-1/2 pl-8">
    //             <h1 className="text-lg font-bold mb-2">Confidence Score</h1>
    //             <h6 className="font-bold" style={{fontSize: "40px"}}>{predictionResult?.accuracy}%</h6>
    //           </div>
    //         </div> : 
    //           <div className="w-1/2">
    //             <h1 className="text-md font-bold mb-2">Anomaly Prediction</h1>
    //             <h6 className="font-bold" style={{fontSize: "40px"}}>{predictionResult?.prediction}</h6>
    //           </div>
    //         }
    //       </div>
    //       }

    //       <div className="relative border-2 rounded-lg p-3 md:p-5">
    //         <form onSubmit={handleSubmit} className="space-y-4">
    //           <div className="hidden md:grid md:grid-cols-3 gap-3 mb-2 text-sm">
    //             <div className="font-semibold px-2">Data Column Name</div>
    //             <div className="font-semibold px-2">Data Type</div>
    //             <div className="font-semibold px-2">Input Value</div>
    //           </div>

    //           <div className="space-y-4 md:space-y-3">
    //             {fields.map((field, index) => (
    //               <div
    //                 key={index}
    //                 className="flex flex-col md:grid md:grid-cols-3 gap-2 md:gap-3 md:items-start min-w-0"
    //               >
    //                 <div className="bg-gray-100 p-2 rounded text-sm break-words min-w-0 overflow-hidden">
    //                   <div className="truncate" title={field.name}>
    //                     {field.name}
    //                   </div>
    //                 </div>

    //                 <div className="bg-gray-100 p-2 rounded text-sm break-words min-w-0 overflow-hidden">
    //                   <div className="truncate" title={field.dataType}>
    //                     {field.dataType}
    //                   </div>
    //                 </div>

    //                 <div className="flex flex-col w-full min-w-0">
    //                   {renderInput(field)}
    //                   {field.error && field.dataType !== "Boolean" && (
    //                     <span className="text-red-500 text-xs mt-1">
    //                       {field.error}
    //                     </span>
    //                   )}
    //                 </div>
    //               </div>
    //             ))}
    //           </div>
    //           <Box
    //             sx={{
    //               display: "flex",
    //               flexWrap: "wrap",
    //               justifyContent: { xs: "center", sm: "flex-center" },
    //               gap: 2,
    //               mt: 2,
    //             }}
    //           >
    //             {" "}
    //             <button
    //               onClick={handleCancel}
    //               className="px-6 py-2 border border-[#4AA3BA] text-[#4AA3BA] rounded-lg hover:bg-gray-50"
    //               type="button"
    //             >
    //               Cancel
    //             </button>
    //             <Button
    //               type="submit"
    //               disabled={isSubmitting}
    //             >
    //               {isSubmitting ? (
    //                 <div className="flex items-center space-x-2">
    //                   <span>Please wait</span>
    //                   <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
    //                 </div>
    //               ) : (
    //                 "Run Prediction"
    //               )}
    //             </Button>
    //           </Box>
    //         </form>


    //       </div>
    //     </>
    //   )}
    //   {isModalOpen && predictionResult && (
    //     <PredictionModal
    //       isOpen={isModalOpen}
    //       onClose={() => setIsModalOpen(false)}
    //       predictionResult={predictionResult}
    //     />
    //   )}
    // </div>
    // </>
  );
};

export default FormPrediction;